import React, { useEffect, useCallback, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { GeneralButton, GeneralDialog } from 'sharedComponents';
import {
    DndContext,
    DragOverlay,
    useSensor,
    useSensors,
    PointerSensor,
    TouchSensor,
    KeyboardSensor,
    closestCenter
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { IconDrag } from 'svgIcons/MotionPortalIcons';

// Extracted components
import SelectedAssetsPanel from './components/SelectedAssetsPanel';
import PhysicalConnectionPanel from './components/PhysicalConnectionPanel';
import SortableAssetItem from './components/SortableAssetItem';
import { AddGearboxDialog } from './components/AddGearboxDialog';

// Custom hooks
import { useAssetManagement, useDragAndDrop } from './hooks/useDragAndDrop';

// Validation utilities
import { validatePhysicalConnection } from './utils/validationUtils';

// Constants
import {
    DRAG_SENSOR_CONFIG,
    PANEL_TITLES,
    ALLOWED_ASSET_TYPES
} from './constants';
import { ASSET_TYPES } from '../../../constants';

import './PhysicalConnectionStep.scss';

// Draggable Asset Item for drag overlay
const DraggableAssetItem = ({ asset, isDragging = false }) => {
    return (
        <div
            className={`asset-item ${isDragging ? 'dragging' : ''}`}
            style={{ opacity: isDragging ? 0.5 : 1 }}
        >
            <div className='asset-drag-handle'>
                <IconDrag width={12} height={12} />
            </div>
            <div className='asset-icon'>
                {/* Asset icon will be rendered by getAssetIcon helper function */}
            </div>
            <div className='asset-details'>
                <div className='asset-name'>{asset.name}</div>
                <div className='asset-id'>({String(asset.assetId)})</div>
                {asset.selectedSubType &&
                    <div className='asset-subtype'>SubType: {asset.selectedSubType}</div>
                }
            </div>
        </div>
    );
};

DraggableAssetItem.propTypes = {
    asset: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        assetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        selectedSubType: PropTypes.string
    }).isRequired,
    isDragging: PropTypes.bool
};

/**
 * PhysicalConnectionStep - Third step for configuring physical connections
 * Loads assets from the AssetSelectionStep and allows drag-and-drop configuration
 * Uses extracted components and custom hooks for cleaner separation of concerns
 * Integrates getAssetDetailsRequest to fetch detailed asset information for validation
 * Includes comprehensive validation with error banner and asset highlighting
 */
const PhysicalConnectionStep = ({
    stepData = {},
    allStepData = {},
    onStepDataChange = () => { },
    onStepComplete = () => { },
    isActive = false,
    // Redux actions passed from parent
    actions = {},
    // Redux state for asset details (available for future use if needed)
    assetDetails = []
}) => {
    const { t: translate } = useTranslation();

    // SINGLE SOURCE OF TRUTH: Get fresh asset data from Step 2 (assetSelection)
    const assetSelectionData = allStepData.assetSelection || {};
    const selectedAssetsFromPreviousStep = assetSelectionData.selectedAssets || [];

    // Get editing powertrain ID from basicInfo step (for edit mode)
    const basicInfoData = allStepData?.basicInfo || {};
    const editingPowertrainId = basicInfoData?.powertrainId;

    // Track if user has made changes in edit mode (to control validation display)
    const [hasUserMadeChanges, setHasUserMadeChanges] = useState(false);
    const initialPhysicalConnectionRef = useRef(null);

    // Add Gearbox dialog state
    const [showAddGearboxDialog, setShowAddGearboxDialog] = useState(false);

    // Remove Gearbox confirmation dialog state
    const [showRemoveGearboxDialog, setShowRemoveGearboxDialog] = useState(false);
    const [pendingRemoveAssetId, setPendingRemoveAssetId] = useState(null);

    // Get organization and site from basicInfo step for pre-filling dialog
    const organizationName = basicInfoData?.selectedOrganization?.organizationName || basicInfoData?.selectedOrganization?.name || '';
    const siteName = basicInfoData?.selectedSite?.siteName || '';


    // Helper function to get asset connection status from Redux assetDetails
    const getAssetConnectionStatus = useCallback((assetId) => {
        const details = assetDetails.find(detail =>
            detail.assetId.toString() === assetId.toString()
        );
        const connectedPowertrainId = details?.powertrain?.id;
        // const connectedAssetId = details?.isConnectedToPowertrain || null;
        // In edit mode, distinguish between same powertrain and other powertrains
        const isConnectedToSamePowertrain = details?.isConnectedToPowertrain &&
            connectedPowertrainId !== null &&
            editingPowertrainId !== null &&
            connectedPowertrainId === editingPowertrainId;

        const isConnectedToAnotherPowertrain = details?.isConnectedToPowertrain &&
            connectedPowertrainId !== null &&
            (editingPowertrainId === null || connectedPowertrainId !== editingPowertrainId);
        return {
            isConnectedToPowertrain: isConnectedToAnotherPowertrain, // Only true for OTHER powertrains
            isConnectedToSamePowertrain, // New flag for same powertrain
            connectedPowertrainName: details?.powertrain?.name,
            powertrainId: connectedPowertrainId
        };
    }, [assetDetails, editingPowertrainId]);

    // ENHANCEMENT: Fetch asset details when selectedAssetsFromPreviousStep changes
    // This ensures we always have fresh data when returning from Step 2
    useEffect(() => {
        if (selectedAssetsFromPreviousStep.length > 0 && actions.getAssetDetailsRequest) {
            const assetsPayload = selectedAssetsFromPreviousStep.map(asset => ({
                assetId: asset.assetId,
                ...asset.selectedSubType && { assetSubType: asset.selectedSubType }
            }));

            actions.getAssetDetailsRequest(assetsPayload);
        }
    }, [selectedAssetsFromPreviousStep.length, actions.getAssetDetailsRequest]);

    // MEMOIZATION: Transform asset details only when data changes
    const transformedAssets = useMemo(() => {
        // Always transform assets from Step 2, even if assetDetails is empty
        // This ensures assets are visible while waiting for asset details to load
        return selectedAssetsFromPreviousStep.map((asset) => {
            const connectionStatus = getAssetConnectionStatus(asset?.assetId);
            return {
                id: `asset-${asset?.assetId}`,
                name: asset?.assetName,
                type: asset?.assetType.toLowerCase(),
                assetId: String(asset?.assetId),
                selectedSubType: asset?.selectedSubType,
                outputSpeedRatio: asset?.outputSpeedRatio || '',
                isConnectedToPowertrain: connectionStatus?.isConnectedToPowertrain, // Only OTHER powertrains
                isConnectedToSamePowertrain: connectionStatus?.isConnectedToSamePowertrain, // Same powertrain
                connectedPowertrainName: connectionStatus?.connectedPowertrainName,
                originalAsset: asset
            };
        });
    }, [selectedAssetsFromPreviousStep, getAssetConnectionStatus, assetDetails]);

    // SINGLE SOURCE OF TRUTH: Combine stepData with fresh asset list from Step 2
    // ENHANCEMENT: Preserve output ratios when updating assets and handle edit mode
    const initialStepData = useMemo(() => {
        // Transform all assets from Step 2 to the internal format
        const selectedAssets = transformedAssets.filter(asset =>
            selectedAssetsFromPreviousStep.some(s => String(s.assetId) === String(asset.assetId))
        );

        // Get physical connection data from stepData (in edit mode, this comes from model?.connectedAssets)
        const physicalConnectionData = Array.isArray(stepData.physicalConnection)
            ? stepData.physicalConnection
            : [];

        // Preserve unmonitored gearboxes that were added via the Add Gearbox dialog.
        // They have no matching Step-2 asset (no real assetId), so they must be
        // passed through as-is instead of being matched against selectedAssets.
        const unmonitoredGearboxes = physicalConnectionData.filter(a => a.isUnmonitoredGearbox);

        // Transform and match physical connection assets with selectedAssets
        // Handle both API format (assetId, assetName, speedRatio) and internal format (id, name, outputSpeedRatio)
        const physicalConnection = physicalConnectionData
            .map(connectedAsset => {
                // Unmonitored gearboxes: keep as-is, no Step-2 match needed
                if (connectedAsset.isUnmonitoredGearbox) {
                    return connectedAsset;
                }

                // Find matching asset in selectedAssets by comparing IDs
                const matchingAsset = selectedAssets.find(asset => {
                    const connectedId = String(connectedAsset?.assetId || connectedAsset?.id?.replace('asset-', ''));
                    return asset?.assetId === connectedId;
                });

                if (matchingAsset) {

                    // Use speedRatio from API data (connectedAsset) if available, otherwise use outputSpeedRatio
                    let preservedRatio;
                    if (connectedAsset?.speedRatio !== undefined && connectedAsset?.speedRatio !== 0) {
                        preservedRatio = connectedAsset.speedRatio;
                    } else if (connectedAsset?.outputSpeedRatio !== undefined && connectedAsset?.outputSpeedRatio !== 0) {
                        preservedRatio = connectedAsset.outputSpeedRatio;
                    } else {
                        preservedRatio = matchingAsset?.outputSpeedRatio || '';
                    }

                    return {
                        ...matchingAsset,
                        outputSpeedRatio: preservedRatio
                    };
                }

                return null;
            })
            .filter(Boolean); // Remove null entries for assets not found in selectedAssets

        // Include unmonitored gearboxes in selectedAssets so that useAssetManagement
        // correctly tracks them (availableAssets = selectedAssets − physicalConnection)
        // and the _version accounts for them so the sync useEffect doesn't reset state.
        const allSelectedAssets = [...selectedAssets, ...unmonitoredGearboxes];

        return {
            outputRatios: stepData.outputRatios || {},
            selectedAssets: allSelectedAssets,
            physicalConnection,
            _version: `${allSelectedAssets.length}-${physicalConnection.length}`
        };
    }, [transformedAssets, selectedAssetsFromPreviousStep, stepData.physicalConnection, stepData.outputRatios, editingPowertrainId]);

    // Custom hooks for asset management and drag/drop logic
    const assetManagement = useAssetManagement(initialStepData, onStepDataChange);
    const dragAndDrop = useDragAndDrop(assetManagement);

    // Store initial physical connection in edit mode to detect changes
    useEffect(() => {
        if (editingPowertrainId && initialPhysicalConnectionRef?.current === null && initialStepData?.physicalConnection?.length > 0) {
            initialPhysicalConnectionRef.current = JSON.stringify(
                initialStepData?.physicalConnection.map(a => ({ id: a.id, outputSpeedRatio: a.outputSpeedRatio }))
            );
        }
    }, [editingPowertrainId, initialStepData?.physicalConnection]);

    // Detect changes made by user in edit mode
    useEffect(() => {
        if (editingPowertrainId && initialPhysicalConnectionRef.current !== null && !hasUserMadeChanges) {
            const currentConnection = JSON.stringify(
                assetManagement?.physicalConnection?.map(a => ({ id: a.id, outputSpeedRatio: a.outputSpeedRatio }))
            );

            if (currentConnection !== initialPhysicalConnectionRef?.current) {
                setHasUserMadeChanges(true);
            }
        }
    }, [editingPowertrainId, assetManagement?.physicalConnection, hasUserMadeChanges]);

    // Clear the isSkipped flag when physical connection step becomes active
    // This ensures validation runs properly when user returns to this step
    useEffect(() => {
        if (isActive && stepData?.isSkipped) {
            onStepDataChange({
                ...stepData,
                isSkipped: false
            });
        }
    }, [isActive, stepData?.isSkipped]);

    // Build output ratios map from selected assets and physical connection assets
    // Must be after assetManagement hook to access physicalConnection
    const outputRatios = useMemo(() => {
        const ratios = {};
        // First, get ratios from selected assets (from previous step)
        selectedAssetsFromPreviousStep?.forEach(asset => {
            if (asset?.outputSpeedRatio) {
                ratios[asset?.assetId] = asset?.outputSpeedRatio;
            }
        });
        // Then, override with ratios from physical connection assets (current state)
        assetManagement?.physicalConnection?.forEach(asset => {
            if (asset?.outputSpeedRatio !== undefined && asset?.outputSpeedRatio !== null) {
                ratios[asset?.assetId] = String(asset?.outputSpeedRatio);
            }
        });
        return ratios;
    }, [selectedAssetsFromPreviousStep, assetManagement?.physicalConnection]);

    // Handle output ratio changes - updates assets directly in the hook
    const handleOutputRatioChange = useCallback((assetId, value) => {
        // Update the asset in the hook's state immediately
        // This will trigger syncToParent with updated outputRatios object
        if (assetManagement.updateAssetOutputRatio) {
            assetManagement.updateAssetOutputRatio(assetId, value);
        }
    }, [assetManagement]);

    // Check if there's already a gearbox in the physical connection
    const hasGearboxInConnection = useMemo(() => {
        return assetManagement.physicalConnection.some(asset =>
            asset.selectedSubType?.toLowerCase() === ASSET_TYPES.GEARBOX
        );
    }, [assetManagement.physicalConnection, ASSET_TYPES]);

    // Handle adding a gearbox from the dialog
    const handleAddGearbox = useCallback((gearboxData) => {
        if (assetManagement.addGearboxToConnection) {
            assetManagement.addGearboxToConnection(gearboxData);
            setShowAddGearboxDialog(false);
            setHasUserMadeChanges(true);
        }
    }, [assetManagement, ASSET_TYPES]);

    // Handle removing an unmonitored gearbox — show confirmation dialog first
    const handleRemoveGearbox = useCallback((assetId) => {
        setPendingRemoveAssetId(assetId);
        setShowRemoveGearboxDialog(true);
    }, []);

    const handleConfirmRemoveGearbox = useCallback(() => {
        if (pendingRemoveAssetId && assetManagement.removeGearboxFromConnection) {
            assetManagement.removeGearboxFromConnection(pendingRemoveAssetId);
        }
        setShowRemoveGearboxDialog(false);
        setPendingRemoveAssetId(null);
    }, [pendingRemoveAssetId, assetManagement]);

    const handleCancelRemoveGearbox = useCallback(() => {
        setShowRemoveGearboxDialog(false);
        setPendingRemoveAssetId(null);
    }, []);

    // Helper function to get validation status for assets in Selected Assets panel
    const getSelectedAssetValidation = useCallback((asset) => {
        const errors = [];
        const warnings = [];

        // Check if asset is already connected to ANOTHER powertrain (not the editing one)
        if (asset?.isConnectedToPowertrain) {
            const message = asset?.connectedPowertrainName
                ? translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_AssetAlreadyConnectedWithName', { powertrainName: asset?.connectedPowertrainName })
                : translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Validation_AssetAlreadyConnected');

            // Add as warning instead of error for better UX in Selected Assets panel
            warnings.push({
                type: 'ALREADY_CONNECTED',
                message,
                severity: 'warning'
            });
        }

        // Check if asset type is allowed in physical connection
        const assetType = asset?.type || asset?.assetType;
        const isAllowedType = ALLOWED_ASSET_TYPES.includes(assetType?.toLowerCase());

        if (!isAllowedType) {
            errors.push({
                type: 'INVALID_ASSET_TYPE',
                message: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_InvalidAssetTypeMessage', { assetType }),
                severity: 'error'
            });
        }

        // Determine if asset can be dragged
        // Can drag if: not connected to another powertrain OR connected to the same powertrain being edited
        const canDrag = !asset?.isConnectedToPowertrain || asset?.isConnectedToSamePowertrain;

        // Show warning icon if connected to another powertrain
        const showWarningIcon = asset?.isConnectedToPowertrain;

        return {
            hasErrors: errors.length > 0,
            hasWarnings: warnings.length > 0, // This will be true for assets connected to other powertrains
            errors,
            warnings,
            canDrag, // Control draggability
            showWarningIcon // Show warning icon for assets connected to other powertrains
        };
    }, [translate]);

    // Validation logic for physical connection
    // Run validations for both create and edit modes, showing errors/warnings in both
    const validationResult = useMemo(() => {
        // Only return null if there are no assets in physical connection
        if (assetManagement?.physicalConnection?.length === 0) {
            return null;
        }

        // If assetDetails not loaded yet, skip validation but don't block rendering
        if (assetDetails?.length === 0) {
            return null;
        }

        const rawValidationResult = validatePhysicalConnection(
            assetManagement.physicalConnection,
            assetDetails,
            outputRatios,
            translate
        );

        // In edit mode, handle validation errors differently to allow users to fix configurations
        if (editingPowertrainId && rawValidationResult) {
            const filteredErrors = [];
            const additionalWarnings = [];

            rawValidationResult?.errors?.forEach(error => {
                // Filter out "already connected" errors for assets connected to the SAME powertrain
                if (error?.type === 'ASSET_ALREADY_CONNECTED' && error?.assetId) {
                    const asset = assetManagement?.physicalConnection.find(a =>
                        String(a.assetId) === String(error?.assetId)
                    );

                    if (asset?.isConnectedToSamePowertrain) {
                        return; // Skip this error
                    }
                }

                // Convert INVALID_DRIVING_CONNECTION errors to warnings in edit mode
                // This allows users to proceed and fix configurations when GM subtypes change
                if (error?.type === 'INVALID_DRIVING_CONNECTION') {
                    additionalWarnings.push({
                        ...error,
                        severity: 'warning',
                        message: `${error?.message} (Configuration needs to be updated)`
                    });
                    return; // Don't add as error
                }

                // Keep all other errors
                filteredErrors.push(error);
            });

            return {
                ...rawValidationResult,
                errors: filteredErrors,
                warnings: [...rawValidationResult?.warnings || [], ...additionalWarnings],
                hasErrors: filteredErrors?.length > 0,
                hasWarnings: (rawValidationResult?.warnings?.length || 0) + additionalWarnings?.length > 0
            };
        }

        return rawValidationResult;
    }, [assetManagement?.physicalConnection, assetDetails, outputRatios, translate, editingPowertrainId]);

    // Configure sensors for drag and drop - called unconditionally at top level (Rules of Hooks)
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: DRAG_SENSOR_CONFIG.ACTIVATION_CONSTRAINT,
        }),
        useSensor(TouchSensor, {
            activationConstraint: DRAG_SENSOR_CONFIG.TOUCH_ACTIVATION_CONSTRAINT,
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleSkip = useCallback(() => {
        // When skipping, treat all selected assets as non-connected
        // Clear physical connection and output ratios
        const skippedStepData = {
            selectedAssets: [], // Clear selected assets from this step
            physicalConnection: [], // Clear physical connections - this ensures all assets go to notConnectedAssets
            outputRatios: {}, // Clear output ratios
            isSkipped: true, // FLAG: This tells WizardContainer to skip validation and allow progression
            // Add minimal data to satisfy validation schema
            availableAssets: [],
            configuredConnections: [],
            connectionSequence: [],
            connectionSettings: {
                allowParallelConnections: false,
                validateConnections: false,
                enforceSequence: false
            }
        };

        // Update step data immediately - this sets isSkipped flag
        onStepDataChange(skippedStepData);

        // Trigger step completion to move to next step automatically
        onStepComplete(skippedStepData);

        // Log to verify update
    }, [assetManagement.physicalConnection.length, selectedAssetsFromPreviousStep.length, onStepDataChange, onStepComplete]);

    // Early return AFTER all hooks — keeps hook call order consistent on every render
    if (selectedAssetsFromPreviousStep?.length === 0) {
        return (
            <div className={`physical-connection-step ${isActive ? 'active' : ''}`}>
                <div className='step-header'>
                    <h2 className='step-title'>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_Title')}</h2>
                </div>
                <div className='no-assets-message'>
                    <p>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_NoAssetsMessage')}</p>
                    <p>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_NoAssetsSubMessage')}</p>
                </div>
            </div>
        );
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={dragAndDrop.handleDragStart}
            onDragOver={dragAndDrop.handleDragOver}
            onDragEnd={dragAndDrop.handleDragEnd}
        >
            <div className={`physical-connection-step ${isActive ? 'active' : ''}`}>
                <div className='step-header'>
                    <h2 className='step-title'>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_Title')}</h2>
                    <GeneralButton
                        type='normal'
                        text={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_SkipButton')}
                        onClick={handleSkip}
                    />
                </div>

                <div className='connection-layout'>
                    {/* Selected Assets Panel */}
                    <SelectedAssetsPanel
                        assets={assetManagement.availableAssets}
                        activeId={dragAndDrop.activeId}
                        title={PANEL_TITLES.SELECTED_ASSETS}
                        onOutputRatioChange={handleOutputRatioChange}
                        isDraggingConnectedAsset={dragAndDrop.isDraggingConnectedAsset}
                        getAssetValidation={getSelectedAssetValidation}
                    />

                    {/* Physical Connection Panel */}
                    <PhysicalConnectionPanel
                        assets={assetManagement.physicalConnection}
                        activeId={dragAndDrop.activeId}
                        title={PANEL_TITLES.PHYSICAL_CONNECTION}
                        validationResult={validationResult}
                        onOutputRatioChange={handleOutputRatioChange}
                        isInvalidDropTarget={dragAndDrop.isInvalidDropTarget}
                        isDraggingConnectedAsset={dragAndDrop.isDraggingConnectedAsset}
                        onAddGearboxClick={() => setShowAddGearboxDialog(true)}
                        hasGearboxInConnection={hasGearboxInConnection}
                        onRemoveGearbox={handleRemoveGearbox}
                    />
                </div>

                {/* Drag Overlay */}
                <DragOverlay>
                    {dragAndDrop.activeAsset ?
                        <SortableAssetItem
                            asset={dragAndDrop.activeAsset}
                            isDragging={true}
                            showDragIndicator={false}
                        />
                        : null}
                </DragOverlay>

                {/* Add Gearbox Dialog */}
                <AddGearboxDialog
                    show={showAddGearboxDialog}
                    onClose={() => setShowAddGearboxDialog(false)}
                    onAdd={handleAddGearbox}
                    organization={organizationName}
                    site={siteName}
                />

                {/* Remove Gearbox Confirmation Dialog */}
                <GeneralDialog
                    show={showRemoveGearboxDialog}
                    close={handleCancelRemoveGearbox}
                    title={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_RemoveGearbox_Title')}
                    closeButton={true}
                    notificationType='info'
                    persistent={true}
                    modalClassName='remove-gearbox-dialog-modal'
                    cancelButtonProps={{
                        text: translate('ABB.Powertrain.Frontend.cancelLabel'),
                        onClick: handleCancelRemoveGearbox
                    }}
                    acceptButtonProps={{
                        text: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_RemoveGearbox_RemoveButton'),
                        onClick: handleConfirmRemoveGearbox
                    }}
                >
                    <p>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_RemoveGearbox_Message')}</p>
                </GeneralDialog>
            </div>
        </DndContext>
    );
};

PhysicalConnectionStep.propTypes = {
    stepData: PropTypes.object,
    allStepData: PropTypes.object,
    onStepDataChange: PropTypes.func,
    onStepComplete: PropTypes.func,
    isActive: PropTypes.bool,
    // Redux actions
    actions: PropTypes.shape({
        getAssetDetailsRequest: PropTypes.func
    }),
    // Redux state for asset details
    assetDetails: PropTypes.array,
    isLoadingAssetDetails: PropTypes.bool,
    assetDetailsError: PropTypes.object
};

export default PhysicalConnectionStep;
